import React, { useEffect, useRef, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import useSignOut from "../../utils/sign-out";
import { useTranslation } from "react-i18next";

const AppPopOver = ({
  isPopoverOpen,
  setIsPopoverOpen,
  image,
  label,
  content,
  subContent,
  classNameList,
  className,
  type,
  accountNo,
  privacyLabel,
  forceDownward = false,
  enableDynamicPositioning = false,
  triggerRef,
}) => {
  const popoverRef = useRef(null);
  const signOut = useSignOut();
  const { t } = useTranslation();
  const [positionStyle, setPositionStyle] = useState({});
  const [isPositioned, setIsPositioned] = useState(false);
  const handleClickOutside = useCallback(
    (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsPopoverOpen(false);
      }
    },
    [setIsPopoverOpen]
  );

  const handleScroll = useCallback(() => {
    setIsPopoverOpen(false);
  }, [setIsPopoverOpen]);

  useEffect(() => {
    if (isPopoverOpen) {
      setIsPositioned(false); // Reset visibility when opening
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (isPopoverOpen) {
      // Add a small delay before attaching scroll listener to prevent flickering
      const timeoutId = setTimeout(() => {
        window.addEventListener("scroll", handleScroll, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isPopoverOpen, handleScroll]);

  useEffect(() => {
    if (!enableDynamicPositioning || !isPopoverOpen || !popoverRef.current)
      return;

    const calculatePosition = () => {
      if (!popoverRef.current) return;

      const popoverRect = popoverRef.current.getBoundingClientRect();

      // Get trigger element position (either from triggerRef or parent element)
      let triggerRect;
      if (triggerRef?.current) {
        triggerRect = triggerRef.current.getBoundingClientRect();
      } else {
        // Try to find the parent button/trigger element
        const parentElement = popoverRef.current.parentElement;
        if (parentElement) {
          triggerRect = parentElement.getBoundingClientRect();
        } else {
          triggerRect = popoverRect;
        }
      }

      const popoverHeight = popoverRect.height;
      const popoverWidth = popoverRect.width;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;

      const newPosition = {};
      newPosition.position = "fixed";
      newPosition.zIndex = 1000;

      // Vertical positioning
      if (forceDownward) {
        // Force below the trigger
        newPosition.top = `${triggerRect.bottom + 8}px`;
        newPosition.bottom = "auto";

        // Ensure it doesn't go below viewport
        if (triggerRect.bottom + 8 + popoverHeight > viewportHeight) {
          newPosition.top = `${Math.max(
            8,
            viewportHeight - popoverHeight - 8
          )}px`;
        }
      } else {
        // Auto-position based on available space
        if (spaceBelow >= popoverHeight + 16 || spaceBelow > spaceAbove) {
          // Place below
          newPosition.top = `${triggerRect.bottom + 8}px`;
          newPosition.bottom = "auto";

          // Ensure it doesn't go below viewport
          if (triggerRect.bottom + 8 + popoverHeight > viewportHeight) {
            newPosition.top = `${Math.max(
              8,
              viewportHeight - popoverHeight - 8
            )}px`;
          }
        } else {
          // Place above
          newPosition.bottom = `${viewportHeight - triggerRect.top + 8}px`;
          newPosition.top = "auto";

          // Ensure it doesn't go above viewport
          if (
            viewportHeight - triggerRect.top + 8 + popoverHeight >
            viewportHeight
          ) {
            newPosition.bottom = `${Math.max(
              8,
              viewportHeight - popoverHeight - 8
            )}px`;
          }
        }
      }

      // Horizontal positioning
      // Try to align with the right edge of the trigger first
      let leftPosition = triggerRect.right - popoverWidth;

      // If that would push it off the left edge, align with left edge of trigger
      if (leftPosition < 8) {
        leftPosition = triggerRect.left;
      }

      // If that would push it off the right edge, align to right edge of viewport
      if (leftPosition + popoverWidth > viewportWidth - 8) {
        leftPosition = viewportWidth - popoverWidth - 8;
      }

      // Final safety check - ensure it's not off the left edge
      leftPosition = Math.max(8, leftPosition);

      newPosition.left = `${leftPosition}px`;
      newPosition.right = "auto";
      newPosition.marginTop = "0";
      newPosition.marginBottom = "0";

      setPositionStyle(newPosition);
      setIsPositioned(true);
    };

    // Calculate position after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(calculatePosition, 0);

    // Recalculate position on resize only (scroll will close the popup)
    window.addEventListener("resize", calculatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculatePosition);
    };
  }, [isPopoverOpen, forceDownward, enableDynamicPositioning, triggerRef]);

  return (
    <>
      {isPopoverOpen && (
        <div
          ref={popoverRef}
          className={`absolute rounded-lg shadow dark:bg-gray-700  ${className} ${
            type !== "Header" ? "w-48 " : "lg:right-10 z-10 right-2"
          }`}
          style={{
            ...positionStyle,
            opacity: enableDynamicPositioning && !isPositioned ? 0 : 1,
            transition: 'opacity 0.1s ease-in-out'
          }}
        >
          {image && (
            <>
              <div className="py-2 text-center text-[12px] flex flex-col items-center max-w-full text-wrap">
                <div
                  className="rounded-full cursor-pointer bg-insightful-light-purple p-1 overflow-hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopoverOpen(false);
                  }}
                >
                  <img
                    src={image}
                    alt="Avatar"
                    className="object-cover rounded-full transition-transform transform hover:scale-110 w-10 h-10"
                  />
                </div>
                <p className="text-insightful-black dark:text-gray-200 font-bold text-sm px-4 whitespace-normal break-all">
                  {label}
                </p>
                <p className="text-insightful-black dark:text-gray-200 font-bold text-[10px] truncate px-4">
                  {t("header.account_number")}: {accountNo}
                </p>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-100" />
            </>
          )}

          <ul
            className={`py-3 text-insightful-black dark:text-gray-200 list-outside ${classNameList}`}
          >
            {content?.map((item, index) => (
              <li key={index}>
                <div
                  onClick={(e) => item.onClick?.(e)}
                  className={`flex items-start cursor-pointer px-4 py-2 hover:bg-insightful-lightest-gray dark:hover:bg-gray-600 dark:hover:text-white ${
                    type === "Header" ? "justify-center text-center" : ""
                  }`}
                >
                  {item.icon && (
                    <img
                      src={item.icon}
                      className="w-4 h-4 mt-1 mr-2"
                      alt={item.label}
                    />
                  )}
                  <span className="leading-tight">{item.label}</span>
                </div>
              </li>
            ))}
          </ul>

          {subContent && (
            <div className="border-t border-gray-100 dark:border-gray-600" />
          )}

          <ul
            className={`text-sm text-insightful-black dark:text-gray-200 ${classNameList}`}
          >
            {subContent?.map((item, index) => (
              <li key={index}>
                <div
                  onClick={(e) => item.onClick?.(e)}
                  className={`block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer ${
                    item.isDestructive ? "text-red-800" : ""
                  }`}
                >
                  {item.icon && (
                    <img
                      src={item.icon}
                      className="w-4 h-4 mr-2 inline-block"
                      alt={item.label}
                    />
                  )}
                  {item.label}
                </div>
              </li>
            ))}
          </ul>

          {type === "Header" && (
            <div className="flex flex-col gap-2">
              <div className="border-t border-gray-100 dark:border-gray-600" />
              <div className="text-center">
                <button
                  className="flex justify-center items-center h-[40px] w-[80%] border border-transparent rounded-full shadow-sm text-[16px] font-bold text-[#444C5E] bg-[#D9D9D9] hover:bg-insightful-lightest-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 mb-4 mx-auto"
                  onClick={() => signOut({ isNavigate: true })}
                >
                  {t("header.signout")}
                </button>
              </div>
            </div>
          )}

          {privacyLabel && (
            <div className="border-t border-gray-100 dark:border-gray-600" />
          )}

          <div className="flex flex-row w-full justify-center items-center gap-2">
            {privacyLabel?.map((item, index) => (
              <React.Fragment key={index}>
                <Link
                  className="text-[12px] text-insightful-black dark:text-gray-200 py-2 cursor-pointer"
                  to={item.path}
                  target="_blank"
                  onClick={() => setIsPopoverOpen(false)}
                >
                  {item.label}
                </Link>
                {index < privacyLabel.length - 1 && (
                  <span className="text-[12px] text-insightful-black dark:text-gray-200 py-2">
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AppPopOver;
